import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Tab,
  Tabs,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  People,
  Category,
  Storage,
  Add,
  Edit,
  Delete,
  Person,
  Warning,
  Backup,
  CheckCircle,
  Cancel,
  Email,
  AccountBalance,
  MergeType,
  LocalOffer,
  CleaningServices,
  SmartToy,
  Psychology,
  Tune,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { usersApi, categoriesApi, databaseApi, subscriptionApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatDate } from '../utils/helpers';
import MergeCategoriesModal from '../components/MergeCategoriesModal';
import LoadingButton from '../components/LoadingButton';
import EmailSettings from '../components/EmailSettings';
import AIModelSettings from '../components/AIModelSettings';
import AccountsManager from '../components/AccountsManager';
import CategoryDialog, { categoryKindLabel } from '../components/CategoryDialog';
import BudgetSettings from './BudgetSettings';
import TagManager from '../components/TagManager';
import AgentManager from '../components/AgentManager';
import GeneralSettings from '../components/GeneralSettings';

const Settings = () => {
  const { showSuccess, showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Dialogs
  const [userDialog, setUserDialog] = useState({ open: false, user: null, mode: 'add' });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null, mode: 'add' });
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  useEffect(() => {
    loadAllData();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await subscriptionApi.getStatus();
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [usersResponse, categoriesResponse, statsResponse] = await Promise.all([
        usersApi.getAll(),
        categoriesApi.getAll({ includeHidden: true }),
        databaseApi.getStats(),
      ]);
      
      setUsers(usersResponse.data.users);
      setCurrentUser(usersResponse.data.currentUser);
      setCategories(categoriesResponse.data.categories);
      setDbStats(statsResponse.data);
    } catch (error) {
      showError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (name) => {
    try {
      if (userDialog.mode === 'add') {
        // Manual user creation is deprecated
        showError('Manual user creation is not allowed. Users are created during sign-up.');
        return;
      } else {
        // Use the new profile update endpoint
        await usersApi.updateProfile({ name });
        showSuccess('Profile updated successfully');
      }
      setUserDialog({ open: false, user: null, mode: 'add' });
      loadAllData();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await usersApi.delete(userId);
      showSuccess('User deleted successfully');
      loadAllData();
    } catch (error) {
      showError('Failed to delete user');
    }
  };

  const handleSaveCategory = async ({ name, kind, groupName, description }) => {
    try {
      if (categoryDialog.mode === 'add') {
        await categoriesApi.create({ name, kind, groupName, description });
        showSuccess('Category created successfully');
      } else {
        const current = categoryDialog.category;
        // Default categories keep their name; send the name only when it changed
        const data = { kind, groupName, description };
        if (!current.isDefault && name !== current.name) data.name = name;
        const response = await categoriesApi.update(current.id, data);
        // Show detailed success message including transaction updates
        if (response.data.updatedTransactions > 0) {
          showSuccess(`Category updated successfully! ${response.data.updatedTransactions} transactions were also updated with the new category name.`);
        } else {
          showSuccess('Category updated successfully');
        }
      }
      setCategoryDialog({ open: false, category: null, mode: 'add' });
      loadAllData();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleBackupDatabase = async () => {
    try {
      const response = await databaseApi.backup();
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense_tracker_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Database backup downloaded');
    } catch (error) {
      showError('Failed to create backup');
    }
  };

  const UsersTab = () => (
    <Box>
      <Card>
        <CardHeader 
          title="Manage Users"
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      {currentUser && user.id === currentUser.id ? (
                        <Chip label="Current" color="success" size="small" />
                      ) : (
                        <Button size="small" onClick={() => usersApi.switch(user.id).then(() => window.location.reload())}>
                          Switch to
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => setUserDialog({ open: true, user, mode: 'edit' })}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={currentUser && user.id === currentUser.id}
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const CategoriesTab = () => {
    const [initializing, setInitializing] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    
    const handleInitializeCategories = async () => {
      try {
        setInitializing(true);
        const response = await categoriesApi.resetDefaults();
        showSuccess(`Initialized ${response.data.createdCount} default categories`);
        loadAllData();
      } catch (error) {
        showError('Failed to initialize categories');
      } finally {
        setInitializing(false);
      }
    };

    const handleMergeComplete = (response) => {
      showSuccess(response.message);
      loadAllData();
    };

    // Hidden categories are old ones kept only for past transactions
    const hiddenCount = categories.filter(c => c.isHidden).length;
    const visibleCategories = showHidden ? categories : categories.filter(c => !c.isHidden);
    
    return (
      <Box>
        <Card>
          <CardHeader 
            title="Manage Categories"
            action={
              <Stack direction="row" spacing={2}>
                {categories.length === 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Backup />}
                    onClick={handleInitializeCategories}
                    disabled={initializing}
                  >
                    {initializing ? 'Initializing...' : 'Initialize Default Categories'}
                  </Button>
                )}
                {categories.length > 1 && (
                  <Button
                    variant="outlined"
                    startIcon={<MergeType />}
                    onClick={() => setMergeModalOpen(true)}
                  >
                    Merge Categories
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCategoryDialog({ open: true, category: null, mode: 'add' })}
                >
                  Add Category
                </Button>
              </Stack>
            }
          />
          <CardContent>
            {categories.length === 0 ? (
              <Alert severity="info">
                No categories found. Click "Initialize Default Categories" to create the default expense categories, 
                or add your own custom categories using the "Add Category" button.
              </Alert>
            ) : (
              <>
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <strong>Kinds</strong> decide which totals a category counts towards. <strong>Spending</strong> counts
                in spending charts and budgets. <strong>Income</strong> is money you earn. <strong>Transfer</strong> is
                money moved between your own accounts, such as a card bill payment. <strong>Investment</strong> is
                money saved or invested. <strong>Refund</strong> is money back for something you bought. <strong>Big
                purchase</strong> is a home or car, kept out of monthly spending. Groups only organise the list.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Group</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleCategories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell>
                          <Typography variant="body2">{category.name}</Typography>
                          {category.description && (
                            <Typography variant="caption" color="text.secondary">{category.description}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{category.groupName || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={categoryKindLabel(category.kind)}
                            size="small"
                            variant="outlined"
                            color={category.kind === 'spending' ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={category.isDefault ? 'Default' : 'Custom'}
                              color={category.isDefault ? 'primary' : 'secondary'}
                              size="small"
                            />
                            {category.isHidden && <Chip label="Hidden" size="small" variant="outlined" />}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setCategoryDialog({ open: true, category, mode: 'edit' })}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={category.isDefault}
                                  onClick={() => categoriesApi.delete(category.id).then(() => loadAllData())}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
            {hiddenCount > 0 && (
              <Button size="small" sx={{ mt: 2 }} onClick={() => setShowHidden(!showHidden)}>
                {showHidden ? 'Hide old categories' : `Show ${hiddenCount} hidden (old) categor${hiddenCount === 1 ? 'y' : 'ies'}`}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Merge Categories Modal */}
        <MergeCategoriesModal
          open={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          categories={categories}
          onMergeComplete={handleMergeComplete}
        />
      </Box>
    );
  };

  const DatabaseTab = () => {
    const [selectedMonths, setSelectedMonths] = useState(3);
    const [oldDataStats, setOldDataStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearing, setClearing] = useState(false);

    const fetchOldDataStats = async (months) => {
      if (!isWhitelisted) return;
      try {
        setLoadingStats(true);
        const response = await databaseApi.getOldDataStats(months);
        setOldDataStats(response.data);
      } catch (error) {
        console.error('Failed to fetch old data stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    const handleMonthsChange = (event) => {
      const months = event.target.value;
      setSelectedMonths(months);
      fetchOldDataStats(months);
    };

    const handleClearOldData = async () => {
      try {
        setClearing(true);
        const response = await databaseApi.clearOldData(selectedMonths, 'CONFIRM_CLEAR');
        showSuccess(response.data.message);
        setClearDialogOpen(false);
        fetchOldDataStats(selectedMonths); // Refresh stats
      } catch (error) {
        showError(error.response?.data?.message || 'Failed to clear old data');
      } finally {
        setClearing(false);
      }
    };

    // Fetch stats on mount for admin users
    React.useEffect(() => {
      if (isWhitelisted) {
        fetchOldDataStats(selectedMonths);
      }
    }, [isWhitelisted]);

    return (
      <Box>
        <Grid container spacing={3}>
          {dbStats && (
            <>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Your Statistics" />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={6} md={4}>
                        <Typography variant="h4" color="success.main">{dbStats.user.transactions.toLocaleString()}</Typography>
                        <Typography variant="body2">Your Transactions</Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="h4" color="info.main">{dbStats.user.categories}</Typography>
                        <Typography variant="body2">Your Categories</Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="h4" color="warning.main">{dbStats.user.files}</Typography>
                        <Typography variant="body2">Your Files</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {isWhitelisted && (
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="System Statistics (Admin)" />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="h4" color="primary">{dbStats.total.users}</Typography>
                          <Typography variant="body2">Total Users</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="h4" color="success.main">{dbStats.total.transactions.toLocaleString()}</Typography>
                          <Typography variant="body2">Total Transactions</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="h4" color="info.main">{dbStats.total.categories}</Typography>
                          <Typography variant="body2">Total Categories</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="h4" color="warning.main">{dbStats.total.files}</Typography>
                          <Typography variant="body2">Total Files</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          )}
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Database Actions" />
              <CardContent>
                <Stack spacing={3}>
                  <Button
                    variant="contained"
                    startIcon={<Backup />}
                    onClick={handleBackupDatabase}
                  >
                    Create Backup
                  </Button>
                  {!isWhitelisted && (
                    <Alert severity="info">
                      More database management features are available. Contact admin for advanced operations.
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Admin-only: Clear Old Data */}
          {isWhitelisted && (
            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  title="Clear Old Temporary Data (Admin)" 
                  subheader="Remove old processed jobs and emails to free up storage"
                />
                <CardContent>
                  <Stack spacing={3}>
                    <Alert severity="info">
                      This will delete reviewed (approved/rejected) processing jobs and processed received emails 
                      older than the selected time period. Your actual transactions are NOT affected.
                    </Alert>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Clear data older than</InputLabel>
                        <Select
                          value={selectedMonths}
                          label="Clear data older than"
                          onChange={handleMonthsChange}
                        >
                          <MenuItem value={3}>3 months</MenuItem>
                          <MenuItem value={6}>6 months</MenuItem>
                          <MenuItem value={12}>1 year</MenuItem>
                        </Select>
                      </FormControl>
                      
                      {loadingStats && <CircularProgress size={24} />}
                    </Box>

                    {oldDataStats && (
                      <Alert severity={oldDataStats.stats.total > 0 ? 'warning' : 'success'}>
                        <Typography variant="subtitle2" gutterBottom>
                          Data older than {oldDataStats.months} months (before {new Date(oldDataStats.cutoffDate).toLocaleDateString()}):
                        </Typography>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                          <li>PDF Processing Jobs: {oldDataStats.stats.pdfJobs}</li>
                          <li>Email Processing Jobs: {oldDataStats.stats.emailJobs}</li>
                          <li>Pending Transactions (will be deleted): {oldDataStats.stats.pendingTransactions}</li>
                          <li>Received Emails: {oldDataStats.stats.receivedEmails}</li>
                        </ul>
                        <Typography variant="body2">
                          <strong>Total items to clear: {oldDataStats.stats.total}</strong>
                        </Typography>
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<CleaningServices />}
                      onClick={() => setClearDialogOpen(true)}
                      disabled={!oldDataStats || oldDataStats.stats.total === 0}
                    >
                      Clear Old Data
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Clear Old Data Confirmation Dialog */}
        <Dialog open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)}>
          <DialogTitle>Clear Old Temporary Data?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete all reviewed processing jobs and processed received emails 
              older than <strong>{selectedMonths} months</strong>.
            </DialogContentText>
            {oldDataStats && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Items to be deleted:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>{oldDataStats.stats.pdfJobs} PDF processing jobs</li>
                  <li>{oldDataStats.stats.emailJobs} Email processing jobs</li>
                  <li>{oldDataStats.stats.pendingTransactions} pending transactions</li>
                  <li>{oldDataStats.stats.receivedEmails} received emails (including S3 files)</li>
                </ul>
              </Alert>
            )}
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              Your actual transactions are NOT affected. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearDialogOpen(false)} disabled={clearing}>
              Cancel
            </Button>
            <Button
              onClick={handleClearOldData}
              color="warning"
              variant="contained"
              disabled={clearing}
              startIcon={clearing ? <CircularProgress size={16} /> : <CleaningServices />}
            >
              {clearing ? 'Clearing...' : 'Clear Old Data'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Check if user is whitelisted
  const isWhitelisted = subscriptionStatus?.status === 'whitelisted';

  // Tabs by name (?tab=email), so other pages can link to one
  const tabs = [
    { key: 'general', label: 'General', icon: <Tune />, render: () => <GeneralSettings /> },
    { key: 'email', label: 'Email', icon: <Email />, render: () => <EmailSettings /> },
    isWhitelisted && { key: 'models', label: 'AI Models', icon: <Psychology />, render: () => <AIModelSettings /> },
    { key: 'categories', label: 'Categories', icon: <Category />, render: () => <CategoriesTab /> },
    { key: 'accounts', label: 'Accounts', icon: <AccountBalanceWallet />, render: () => <AccountsManager /> },
    { key: 'tags', label: 'Tags', icon: <LocalOffer />, render: () => <TagManager /> },
    { key: 'budget', label: 'Budget', icon: <AccountBalance />, render: () => <BudgetSettings /> },
    { key: 'agents', label: 'AI Agents', icon: <SmartToy />, render: () => <AgentManager /> },
    { key: 'database', label: 'Database', icon: <Storage />, render: () => <DatabaseTab /> },
    isWhitelisted && { key: 'users', label: 'Users', icon: <People />, render: () => <UsersTab /> },
  ].filter(Boolean);
  const requestedTab = searchParams.get('tab');
  const activeTab = tabs.find(t => t.key === requestedTab) ? requestedTab : 'general';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Settings</Typography>
      
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setSearchParams({ tab: value }, { replace: true })}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map(t => <Tab key={t.key} value={t.key} label={t.label} icon={t.icon} />)}
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Only the active tab is rendered */}
          {tabs.find(t => t.key === activeTab).render()}
        </Box>
      </Card>

      {/* User Dialog */}
      <FormDialog
        open={userDialog.open}
        title={userDialog.mode === 'add' ? 'Add User' : 'Edit User'}
        label="User Name"
        defaultValue={userDialog.user?.name || ''}
        onClose={() => setUserDialog({ open: false, user: null, mode: 'add' })}
        onSave={handleSaveUser}
      />

      {/* Category Dialog */}
      <CategoryDialog
        open={categoryDialog.open}
        category={categoryDialog.mode === 'edit' ? categoryDialog.category : null}
        groups={[...new Set(categories.map(c => c.groupName).filter(Boolean))].sort()}
        onClose={() => setCategoryDialog({ open: false, category: null, mode: 'add' })}
        onSave={handleSaveCategory}
      />
    </Box>
  );
};

// Reusable Form Dialog
const FormDialog = ({ open, title, label, defaultValue, onClose, onSave, showTransactionWarning = false }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(defaultValue || '');
  }, [defaultValue, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    try {
      await onSave(value.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {showTransactionWarning && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Changing this category name will automatically update all transactions that use this category.
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            label={label}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={saving}
            disabled={!value.trim()}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Settings;